import Button from '@/components/Button';
import EmptyBlock from '@/components/EmptyBlock';
import GsuLoader from '@/components/GsuLoader';
import Layout, { Stack } from '@/components/Layout';
import LottieImage from '@/components/LottieImage';
import Text from '@/components/Text';
import { FragPageMetaInfo } from '@/compositions/MetaInfo/query';
import { Props as PageComposerProps } from '@/compositions/PageComposer';
import PageLayout from '@/compositions/PageLayout';
import { FragScheduleDay } from '@/compositions/ScheduleBlock/query';
import { useAuth } from '@/context/auth';
import { useUI } from '@/context/ui';
import { getTzName } from '@/lib/datetime';
import { queryGsuApi } from '@/service/query';
import { MetaInfo, Page, SchedulePageRequest, SchedulePageSection } from '@/shared/api-generated-types';
import { FragSessionCard } from '@/widgets/ClassCard/query';
import LoginSignUpPrompt from '@/widgets/LoginSignUpPrompt';
import Overlay from '@/widgets/Overlay';
import { FragFacetInfo } from '@/widgets/ScheduleFilters/query';
import ScheduleFiltersMobile from '@/widgets/ScheduleFilters/ScheduleFiltersMobile';
import ScrollTabs from '@/widgets/ScheduleScrollTabs';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { GetServerSideProps, NextPage } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { animated, useTransition } from 'react-spring';
import { gql, useQuery } from 'urql';
dayjs.extend(relativeTime);
let scroll = 0;
const PageComposerWithLoader = dynamic<PageComposerProps>(() => import('@/compositions/PageComposer'), {
  ssr: false,
  loading: () => <GsuLoader consumeSpace />
});
const QUERY_VARS = (startFrom: string, sessionsIntervalStart?: number, sessionsIntervalEnd?: number): Partial<SchedulePageRequest> => {
  let startTime = dayjs(startFrom).startOf('day').format();
  if (dayjs().format('YYYY-MM-DD') === dayjs(startFrom).format('YYYY-MM-DD')) {
    startTime = new Date().toISOString();
  }
  return {
    timezone: getTzName(),
    startFrom: startTime,
    sessionsIntervalStart,
    sessionsIntervalEnd
  };
};
const QUERY_VARS_MY_SCHEDULE = {
  timezone: getTzName(),
  startFrom: new Date().toISOString()
};
const QUERY_SCHEDULE = gql`
  ${FragSessionCard}
  ${FragScheduleDay}
  ${FragFacetInfo}
  ${FragPageMetaInfo}
  query SchedulePage($request: SchedulePageRequest!) {
    page: schedulePage(request: $request) {
      id
      title
      metaInfo {
        ...PageMetaInfo
      }
      sections {
        id
        ... on SchedulePageSection {
          pageInfo {
            totalItems
            hasPrevPage
            hasNextPage
            page
          }
          facetInfo {
            ...FacetInfo
          }
          items {
            ...ScheduleDay
          }
        }
      }
    }
  }
`;
const QUERY_MY_SCHEDULE = gql`
  ${FragSessionCard}
  ${FragScheduleDay}
  ${FragPageMetaInfo}
  query MySchedulePage($request: SchedulePageRequest!) {
    page: mySchedulePage(request: $request) {
      id
      title
      metaInfo {
        ...PageMetaInfo
      }
      sections {
        id
        ... on SchedulePageSection {
          items {
            ...ScheduleDay
          }
        }
      }
    }
  }
`;
const filtersMenu = "f58r2xg";
export const iconAnimationContainer = "i1wihagf";
export const iconAnimation = "iizeggc";
const ScheduleMobile: NextPage<{
  metaInfo: MetaInfo;
}> = _props => {
  const router = useRouter();
  const {
    t
  } = useTranslation('common');
  const {
    state: {
      schedulePage,
      scheduleFilters: scheduleFiltersState
    },
    dispatch
  } = useUI();
  const {
    state: {
      user
    }
  } = useAuth();
  const {
    scheduleType
  } = router.query;
  const whichTab = scheduleType === 'browse' ? ScheduleTab.BROWSE : ScheduleTab.MY_SCHEDULE;
  const isBrowseView = whichTab === ScheduleTab.BROWSE;
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [filtersVisible, setFiltersVisible] = useState<boolean>(false);

  // BrowseSchedule data
  const query_vars = useMemo(() => QUERY_VARS(schedulePage.dayString, schedulePage.sessionsIntervalStart, schedulePage.sessionsIntervalEnd), [schedulePage.dayString, schedulePage.sessionsIntervalStart, schedulePage.sessionsIntervalEnd]);
  const isMyScheduleView = whichTab === ScheduleTab.MY_SCHEDULE;
  const [{
    data: browseData,
    fetching: browseFetching
  }] = useQuery({
    query: QUERY_SCHEDULE,
    variables: {
      request: {
        ...query_vars,
        page: currentPage,
        facets: schedulePage.facets,
        maxItemsPerSection: 20
      }
    },
    // Only fetch browse schedule data if we're on the browse tab
    pause: whichTab !== ScheduleTab.BROWSE
  });
  const browsePageData = browseData?.page || {};

  // MySchedule data
  const [{
    data: myData,
    fetching: myFetching
  }] = useQuery({
    query: QUERY_MY_SCHEDULE,
    variables: {
      request: {
        ...QUERY_VARS_MY_SCHEDULE
      }
    },
    // Only fetch my schedule data if we're on the my-schedule tab
    pause: whichTab !== ScheduleTab.MY_SCHEDULE
  });
  const myPageData: Page = myData?.page || {};
  const hasBookedSessions = useMemo(() => (myPageData?.sections as SchedulePageSection[])?.[0]?.items?.length, [myData]);
  const onScroll = (): void => {
    if (typeof window === 'undefined' || !document) {
      return;
    }
    const el = document.getElementById('schedule-page-tabs');
    if (!el) return;
    if (window.scrollY < scroll) {
      el.style.top = '0px';
    } else {
      el.style.top = '-0px';
    }
    scroll = window.scrollY;
  };
  useEffect(() => {
    // Listeners for show and hide tabs on scroll
    window.removeEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  // Pagination event handler
  const handleLoadMore = useCallback((idx: number, page: number) => {
    setCurrentPage(page);
    dispatch({
      type: 'SET_ACTIVE_SCHEDULE_DATE',
      payload: {
        ...schedulePage,
        index: page
      }
    });
  }, [schedulePage]);
  const tabData = useMemo(() => [{
    tabTitle: t('mySchedule.browseClasses')
  }, {
    tabTitle: t('mySchedule.tabTitle')
  }], [user]);

  // Route flipping between Schedule & My Schedule
  const handleScheduleRoute = (index: number): void => {
    const destinationTab = index === ScheduleTab.BROWSE ? 'browse' : 'my-schedule';
    router.push(`/schedule/${destinationTab}`);
  };

  // Toggle the FilterMenu
  const toggleFilters = useCallback(() => {
    setFiltersVisible(s => !s);
  }, [scheduleType, browseData]);
  const sliderMenuTransition = useTransition(filtersVisible, {
    from: {
      x: '0%',
      y: '100%',
      opacity: 1
    },
    enter: {
      x: '0%',
      y: '0%',
      opacity: 1
    },
    leave: {
      x: '0%',
      y: '100%',
      opacity: 1
    }
  });
  const handleFiltersChange = useCallback(() => {
    setCurrentPage(0);
    toggleFilters();
  }, []);

  // Loading facet filters only during the inital schedule sessions fetch from graphql
  useEffect(() => {
    try {
      if (!browseData || scheduleFiltersState.categories.length > 0 || scheduleFiltersState.guides.length > 0) return;
      const facets = browseData?.page?.sections[0]?.facetInfo.facetsAvailable;
      dispatch({
        type: 'SET_SCHEDULE_FILTERS',
        payload: {
          categories: facets.categories,
          guides: facets.guides
        }
      });
    } catch (error) {
      console.log('Issue getting filters', {
        error
      });
    }
  }, [isBrowseView, browseData]);
  return <PageLayout disablePageContentBottomPadding>
      {sliderMenuTransition((style, item) => item && <>
              <animated.div style={{
        ...style
      }} className={filtersMenu}>
                <ScheduleFiltersMobile fetching={browseFetching} onFilterChange={handleFiltersChange} />
              </animated.div>
              <Overlay zIndex={8} visible={filtersVisible} onClick={toggleFilters} />
            </>)}
      <ScrollTabs onFiltersClicked={toggleFilters} defaultTabIndex={whichTab} tabIndex={whichTab} data={tabData} onTabChange={handleScheduleRoute} activeFiltersCount={schedulePage.facets.length || 0} hideTabs />
      {isBrowseView ? <section className="">
          <PageComposerWithLoader {...browsePageData} onLoadMoreByPageNumber={handleLoadMore} fetching={browseFetching} distinction={`${schedulePage.facets.join('|')}`} />
        </section> : null}
      {/* Show the following prompt only after the GQL query is complete to non-signed-in users */}
      {isMyScheduleView && !user && !myFetching && <LoginSignUpPrompt title={t('mySchedule.prompt')} caption={t('mySchedule.caption')} ctaText={t('mySchedule.ctaText')} />}
      {/* For signed-in users, either show their booked sessions OR empty screen when they've no upcoming sessions */}
      {isMyScheduleView && user && <>
          {myFetching || hasBookedSessions ? <div>
              <PageComposerWithLoader {...myPageData} fetching={myFetching} />
            </div> : <EmptyBlock>
              <Layout>
                <Stack y alignItems="center" justifyContent="space-between">
                  <div className={`${iconAnimationContainer} mb-sm`}>
                    <LottieImage autoplay loop className={iconAnimation} name="calendar-animation" path={'https://ddq2bomue87t4.cloudfront.net/dialog-icons/calendar.json'} />
                  </div>
                  <Text tag="h4" size="md" weight="lg" className="flex-center mb-xxxs">
                    {t('mySchedule.noSessions')}
                  </Text>
                  <Text tag="p" size="xs" weight="sm">
                    {t('mySchedule.noSessionsSubtitle')}
                  </Text>
                  <Button label={t('mySchedule.browseClasses')} theme="primary" size="md" onClick={() => handleScheduleRoute(0)} className="mt-md" />
                </Stack>
              </Layout>
            </EmptyBlock>}
        </>}
    </PageLayout>;
};
export const getServerSideProps: GetServerSideProps = async ({
  locale = 'en-US',
  query,
  res,
  req
}) => {
  if (query && !['browse', 'my-schedule'].includes(`${query?.scheduleType}`)) {
    res.statusCode = 404;
    return {
      notFound: true
    };
  }
  const [urqlState,, data] = await queryGsuApi(query?.scheduleType === 'browse' ? QUERY_SCHEDULE : QUERY_MY_SCHEDULE, {
    request: {
      ...QUERY_VARS(new Date().toISOString()),
      page: 0
    }
  }, req);
  return {
    props: {
      ...(await serverSideTranslations(locale, ['badge', 'common', 'classes'])),
      urqlState,
      metaInfo: data?.page?.metaInfo || ''
    }
  };
};
enum ScheduleTab {
  BROWSE,
  MY_SCHEDULE,
}
export default ScheduleMobile;

require("./ScheduleMobile.linaria.module.css!=!../../../../node_modules/linaria/node_modules/@linaria/webpack4-loader/lib/outputCssLoader.js?cacheProvider=!./ScheduleMobile.tsx");